import type {
  UiInteraction,
  UiSemanticElement,
  WorkspaceSurfaceContent,
  WorkspaceViewScope,
} from '@streamlain/copilot-realtime-contract';
import {
  WORKSPACE_VIEW_MAX_INTERACTIONS,
  WORKSPACE_VIEW_MAX_NEIGHBOURS,
} from '@streamlain/copilot-realtime-contract';

import { projectSemanticInteraction } from './semantic-dom-context';
import type { WorkspaceViewContextPort } from './workspace-view-context';

const origins = new WeakMap<HTMLElement, 'user' | 'ai'>();

/** Same bounded projection for a local capture and the combined active regions. */
export function projectSurfaceInteractions(
  interactions: readonly UiInteraction[]
): UiInteraction[] {
  const ordered = [...interactions].sort((a, b) => a.sequence - b.sequence);
  const recent = ordered.slice(-WORKSPACE_VIEW_MAX_INTERACTIONS);
  const lastUser = ordered.findLast(
    interaction => interaction.origin === 'user'
  );
  if (lastUser && !recent.includes(lastUser)) {
    return [lastUser, ...recent.slice(1)];
  }
  return recent;
}

export function withSurfaceInteractionOrigin(
  root: HTMLElement,
  origin: 'user' | 'ai',
  activate: () => void
): void {
  const previous = origins.get(root);
  origins.set(root, origin);
  try {
    activate();
  } finally {
    if (previous) origins.set(root, previous);
    else origins.delete(root);
  }
}

export function installSurfaceInteractionCapture(
  root: HTMLElement,
  scope: WorkspaceViewScope,
  ownerId: string,
  port: WorkspaceViewContextPort,
  expectedRead?: () => WorkspaceSurfaceContent
): {
  read(): readonly UiInteraction[];
  project(elements: readonly UiSemanticElement[]): readonly UiInteraction[];
  dispose(): void;
} {
  let disposed = false;
  // This is the existing eight-entry ring, retaining only local references and origin.
  const ring: Array<{ element: Element; interaction: UiInteraction }> = [];
  const capture = (element: Element, kind: UiInteraction['kind']) => {
    if (disposed || !root.isConnected) return;
    const admitted = projectSemanticInteraction(element, root, scope);
    if (!admitted) return;
    const current = port.readRevision(scope, ownerId, expectedRead);
    if (!current) return;
    const interaction: UiInteraction = {
      sequence: current.sequence + 1,
      kind,
      origin: origins.get(root) ?? 'user',
      target: admitted.semantic,
      neighbours: [],
    };
    const previous = ring.slice();
    ring.push({ element: admitted.element, interaction });
    const recent = projectSurfaceInteractions(
      ring.map(entry => entry.interaction)
    );
    const keep = new Set(recent);
    for (let index = ring.length - 1; index >= 0; index--)
      if (!keep.has(ring[index].interaction)) ring.splice(index, 1);
    if (!port.invalidate(scope, ownerId, expectedRead))
      ring.splice(0, ring.length, ...previous);
  };
  const click = (event: Event) => {
    // The browser supplies the composed path. Inspect only its first local target.
    const target = event.composedPath()[0];
    if (target instanceof Element) capture(target, 'click');
  };
  const selection = () => {
    const selected = root.ownerDocument.getSelection();
    const anchor = selected?.anchorNode;
    if (!selected || selected.isCollapsed || !anchor) return;
    const element = anchor instanceof Element ? anchor : anchor.parentElement;
    if (element) capture(element, 'selection');
  };
  root.addEventListener('click', click, true);
  root.ownerDocument.addEventListener('selectionchange', selection);
  return {
    read: () =>
      disposed ? [] : structuredClone(ring.map(entry => entry.interaction)),
    project: elements => {
      if (disposed || !root.isConnected) return [];
      // One admitted collector result; no neighbour/descendant DOM reads here.
      const admitted = elements.slice(0, 256);
      const indexes = new Map(
        admitted.map((entry, index) => [entry.key, index])
      );
      const interactions: UiInteraction[] = [];
      for (const entry of ring) {
        if (!projectSemanticInteraction(entry.element, root, scope)) continue;
        const index = indexes.get(entry.interaction.target.key);
        if (index === undefined) continue;
        const neighbours = [admitted[index - 1], admitted[index + 1]]
          .filter((value): value is UiSemanticElement => !!value)
          .slice(0, WORKSPACE_VIEW_MAX_NEIGHBOURS);
        interactions.push({
          ...entry.interaction,
          target: admitted[index],
          neighbours,
        });
      }
      return projectSurfaceInteractions(interactions);
    },
    dispose: () => {
      disposed = true;
      ring.length = 0;
      origins.delete(root);
      root.removeEventListener('click', click, true);
      root.ownerDocument.removeEventListener('selectionchange', selection);
    },
  };
}
